import { BasePaginatorDto } from '@diritto/common/base/paginator/paginator.dto';
import { ResponseDto } from '@diritto/common/base/response.dto';
import { FancyShareCreateDto, FancyShareUpdateDto } from '@diritto/common/fancyShare/dtos/fancy-share-edit.dtos';
import { FancyShareFileResponseDto } from '@diritto/common/fancyShare/dtos/fancy-share-file.dtos';
import { FancyShareLikeResponseDto } from '@diritto/common/fancyShare/dtos/fancy-share-like.dtos';
import {
  FancyShareCanUploadResponseDto,
  FancyShareResponseDto,
  FancySharesRequestDto,
  FancySharesResponseDto,
} from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { Http } from '@diritto/common/utils/requester/http';
import { generateUrl } from '@diritto/common/utils/urls';

export class FancyShareApi {
  constructor(
    protected url: string,
    protected http: Http,
  ) {}

  async createFancyShare(contentId: number, episodeId: number, data: FancyShareCreateDto) {
    const res = await this.http.post<FancyShareCreateDto, FancyShareResponseDto>(
      `${this.url}/fancy-shares/contents/${contentId}/episodes/${episodeId}`,
      data,
    );
    return res.data;
  }

  async checkCanUpload(contentId: number, episodeId: number) {
    const res = await this.http.get<FancyShareCanUploadResponseDto>(
      `${this.url}/fancy-shares/contents/${contentId}/episodes/${episodeId}/check`,
    );
    return res.data;
  }

  async updateFancyShare(fancyShareId: number, data: FancyShareUpdateDto) {
    const res = await this.http.patch<FancyShareUpdateDto, ResponseDto>(
      `${this.url}/fancy-shares/${fancyShareId}`,
      data,
    );
    return res.data;
  }

  async deleteFancyShare(fancyShareId: number) {
    const res = await this.http.delete<ResponseDto>(`${this.url}/fancy-shares/${fancyShareId}`);
    return res.data;
  }

  async uploadFancyShareFile(data: FormData) {
    const res = await this.http.post<FormData, FancyShareFileResponseDto>(
      `${this.url}/fancy-shares/files/images`,
      data,
    );
    return res.data;
  }

  async getFancyShare(fancyShareId: number) {
    const res = await this.http.get<FancyShareResponseDto>(`${this.url}/fancy-shares/${fancyShareId}`);
    return res.data;
  }

  async findByProfileId(profileId: number, includeAdult: boolean, paginator?: BasePaginatorDto) {
    const res = await this.http.get<FancySharesResponseDto>(`${this.url}/fancy-shares/profiles/${profileId}`, {
      params: {
        includeAdult,
        ...paginator,
      },
    });
    return res.data;
  }

  async findByContentId(contentId: number, includeAdult: boolean, paginator?: BasePaginatorDto) {
    const res = await this.http.get<FancySharesResponseDto>(`${this.url}/fancy-shares/contents/${contentId}`, {
      params: {
        includeAdult,
        ...paginator,
      },
    });
    return res.data;
  }

  async findByIncludeAdult(data: FancySharesRequestDto, paginator?: BasePaginatorDto) {
    const res = await this.http.get<FancySharesResponseDto>(
      generateUrl(`${this.url}/fancy-shares`, {
        ...data,
        ...paginator,
      }),
    );

    return res.data;
  }

  async likeFancyShare(fancyShareId: number) {
    const res = await this.http.post<undefined, ResponseDto>(`${this.url}/fancy-shares/${fancyShareId}/like`);
    return res.data;
  }

  async unlikeFancyShare(fancyShareId: number) {
    const res = await this.http.delete<ResponseDto>(`${this.url}/fancy-shares/${fancyShareId}/like`);
    return res.data;
  }

  async findFancyShareLikes(fancyShareIds: number[]) {
    const res = await this.http.get<FancyShareLikeResponseDto>(
      `${this.url}/fancy-shares/likes?fancyShareIds=${fancyShareIds.join(',')}`,
    );
    return res.data;
  }
}
